import { inject, Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

import { MsalService } from '@azure/msal-angular'
import { newUser, User } from '@app/interfaces/user'
import { UsersApi } from '@app/apis/users.api'
import { MsalInitService } from '@app/auth/msal-init.service'

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly authService = inject(MsalService)
  private readonly msalInit = inject(MsalInitService)
  private readonly usersApi = inject(UsersApi)

  private user = new BehaviorSubject<User>(newUser())

  constructor () {
    // attempt to get the user from localstorage on creation
    const tempUser = UserService._getLocalStorageUser()

    if (!tempUser.anonymous) {
      // we found a user in local storage, broadcast to consumers
      this.user.next(tempUser)
    }

    this.reloadPermissions()
  }

  private static _getLocalStorageUser (): User {
    let localUser = newUser()
    const userJSON = localStorage.getItem('user')
    if (userJSON) {
      try {
        localUser = JSON.parse(userJSON)
      } catch (e) {
        console.error('Error getting data from localStorage', e)
      }
    }
    return localUser
  }

  setUser (djangoResponse: User) {
    const tempUser = djangoResponse
    tempUser.anonymous = false
    localStorage.setItem('user', JSON.stringify(tempUser))
    this.user.next(tempUser)
  }

  /**
   * reload the perms for the current user, if there is a user logged in.
   */
  reloadPermissions () {
    // UserService is built while resolving the ErrorHandler, i.e. before the MSAL app initializer
    // has run, so wait on the shared init: account reads need the localStorage cache decrypted.
    void this.msalInit.ready.then(() => {
      if (!this.authService.instance.getAllAccounts() || this.authService.instance.getAllAccounts().length === 0) return

      const username = this.authService.instance.getAllAccounts()[0].localAccountId
      this.usersApi.retrieve(username).subscribe((res: User) => {
        this.setUser(res)
      })
    })
  }

  clearUser () {
    localStorage.removeItem('user')
    sessionStorage.clear()
    this.user.next(newUser())
  }

  getUser () {
    return this.user.asObservable()
  }

  hasPermissionIn (permissions: string[]): boolean {
    if (!permissions) return true
    if (permissions.length === 0) return true

    for (const permission of permissions) {
      if (this.user.value.permissions.indexOf(permission) > -1) {
        return true
      }
    }
    return false
  }
}
